/** Mã màn hình — dùng khi lưu cài đặt ẩn/hiện cột theo user. */
export const CUSTOMER_MANAGEMENT_SCREEN_KEY = "quan-li-khach-hang";
export const ORDER_MANAGEMENT_SCREEN_KEY = "quan-li-don-hang";
export const TTH_ORDER_MANAGEMENT_SCREEN_KEY = "quan-li-don-tth";

/** Cột có thể ẩn/hiện trên màn Quản lý khách hàng (# luôn hiển thị). */
export const CUSTOMER_MANAGEMENT_COLUMNS = [
  { key: "email", label: "Email" },
  { key: "username", label: "Tên đăng nhập" },
  { key: "name", label: "Tên" },
  { key: "levelName", label: "Bảng giá" },
  { key: "userContacts", label: "Lines - Đối tác" },
  { key: "lastOrderDate", label: "Đơn cuối" },
  { key: "avgOrder", label: "Trung bình" },
  { key: "customerMinWeightKg", label: "Tối thiểu" },
  { key: "userRank", label: "Thành tích" },
  { key: "customerCode", label: "Mã KH" },
  { key: "childUserCount", label: "Số user" },
  { key: "createdDate", label: "Ngày" },
  { key: "actions", label: "Thao tác" },
];

/** Cột có thể ẩn/hiện trên màn Danh sách đơn hàng (theo thứ tự bảng). */
export const ORDER_MANAGEMENT_COLUMNS = [
  { key: "select", label: "Chọn" },
  { key: "orderId", label: "Mã đơn" },
  { key: "createdDate", label: "Ngày" },
  { key: "lineName", label: "Line" },
  { key: "sackCode", label: "Mã bao" },
  { key: "cnShippingCode", label: "Mã vận đơn TQ" },
  { key: "vnShippingCode", label: "Mã vận VN" },
  { key: "productName", label: "Tên đơn hàng" },
  { key: "packageCount", label: "Kiện" },
  { key: "feeOfBase", label: "kg/m3 - Đ.vị" },
  { key: "priceInsurance", label: "B.hiểm" },
  { key: "shippingMethod", label: "Phương thức lấy hàng" },
  { key: "note", label: "Ghi chú" },
  { key: "progress", label: "Lưu trình" },
  { key: "customerCode", label: "Mã KH" },
  { key: "customerFeeOriginal", label: "ĐS KH gốc" },
  { key: "customerChecking", label: "Đối soát KH" },
  { key: "partnerCode", label: "Mã ĐT" },
  { key: "contactFee", label: "Đối soát ĐT" },
  { key: "profit", label: "Lợi nhuận" },
  { key: "actions", label: "Thao tác" },
];

/** Cột màn "Đơn TTH" — thứ tự theo đúng yêu cầu nghiệp vụ (ID → Ngày tạo → Mã đơn → ...). */
export const TTH_ORDER_MANAGEMENT_COLUMNS = [
  { key: "id", label: "ID" },
  { key: "createdDate", label: "Ngày tạo" },
  { key: "orderCode", label: "Mã đơn" },
  { key: "customerCode", label: "Mã KH" },
  { key: "partnerCode", label: "Mã Đối tác" },
  { key: "quantity", label: "Số lượng Tệ" },
  { key: "customerRate", label: "Tỷ giá KH" },
  { key: "partnerRate", label: "Tỷ giá ĐT" },
  { key: "amountCustomer", label: "VNĐ KH" },
  { key: "amountPartner", label: "VNĐ ĐT" },
  { key: "amountDiff", label: "Chênh lệch" },
  { key: "statusName", label: "Trạng thái đơn" },
  { key: "statusRateName", label: "Trạng thái đối soát" },
  { key: "actions", label: "Thao tác" },
];

/** Khóa cũ localStorage — «Ẩn cột: Xóa» chỉ ẩn nút xóa, không ẩn cả cột Thao tác. */
export const ORDER_LEGACY_DELETE_COLUMN_KEY = "delete";

/** Đổi khóa cũ `shippingCode` (một cột) → TQ + VN. */
export function normalizeOrderScreenHiddenColumns(hiddenCols) {
  if (!Array.isArray(hiddenCols) || hiddenCols.length === 0) return hiddenCols || [];
  if (!hiddenCols.includes("shippingCode")) return hiddenCols;
  const set = new Set(hiddenCols.filter((k) => k !== "shippingCode"));
  set.add("cnShippingCode");
  set.add("vnShippingCode");
  return [...set];
}

const SCREEN_COLUMNS = {
  [CUSTOMER_MANAGEMENT_SCREEN_KEY]: CUSTOMER_MANAGEMENT_COLUMNS,
  [ORDER_MANAGEMENT_SCREEN_KEY]: ORDER_MANAGEMENT_COLUMNS,
};

/**
 * @param {string} screenKey
 * @param {{ key: string, label: string }[]} [columns]
 */
export function getScreenColumnDefinitions(screenKey, columns) {
  if (columns?.length) return columns;
  return SCREEN_COLUMNS[screenKey] || [];
}

/**
 * @param {string} screenKey
 * @param {{ key: string, label: string }[]} columns
 * @param {string[]} [lockedKeys] — cột không cho chỉnh (vd. ẩn theo quyền)
 */
export function getToggleableScreenColumns(screenKey, columns, lockedKeys = []) {
  const defs = getScreenColumnDefinitions(screenKey, columns);
  const locked = new Set(lockedKeys || []);
  return defs.filter((c) => !locked.has(c.key));
}

function validKeySet(screenKey, columns) {
  const keys = new Set(getScreenColumnDefinitions(screenKey, columns).map((c) => c.key));
  if (screenKey === ORDER_MANAGEMENT_SCREEN_KEY) {
    keys.add(ORDER_LEGACY_DELETE_COLUMN_KEY);
  }
  return keys;
}

/**
 * @param {import('axios').AxiosInstance} axiosInstance
 * @param {string} screenKey
 * @param {{ key: string, label: string }[]} [columns]
 * @returns {Promise<string[]>}
 */
export async function fetchScreenHiddenColumns(axiosInstance, screenKey, columns) {
  const validKeys = validKeySet(screenKey, columns);
  try {
    const res = await axiosInstance.get("/api/users/preference/screenColumns", {
      params: { screen: screenKey },
    });
    if (!res?.data?.success) return [];
    const list = res.data.data || [];
    const filtered = list.filter((k) => validKeys.has(k));
    return screenKey === ORDER_MANAGEMENT_SCREEN_KEY
      ? normalizeOrderScreenHiddenColumns(filtered)
      : filtered;
  } catch {
    return [];
  }
}

/**
 * @param {import('axios').AxiosInstance} axiosInstance
 * @param {string} screenKey
 * @param {string[]} hiddenColumns
 * @param {{ key: string, label: string }[]} [columns]
 */
export async function saveScreenHiddenColumns(axiosInstance, screenKey, hiddenColumns, columns) {
  const validKeys = validKeySet(screenKey, columns);
  let normalized = (hiddenColumns || []).filter((k) => validKeys.has(k));
  if (screenKey === ORDER_MANAGEMENT_SCREEN_KEY) {
    normalized = normalizeOrderScreenHiddenColumns(normalized);
  }
  const res = await axiosInstance.put("/api/users/preference/screenColumns", {
    screen: screenKey,
    hiddenColumns: normalized,
  });
  return res?.data;
}

export function isScreenColumnHidden(hiddenCols, columnKey) {
  return Array.isArray(hiddenCols) && hiddenCols.includes(columnKey);
}

export function screenColumnClass(hiddenCols, columnKey) {
  return isScreenColumnHidden(hiddenCols, columnKey) ? "d-none" : "";
}

/**
 * @param {string[]} hiddenCols
 * @param {{ key: string, label: string }[]} columns
 * @param {number} [fixedVisibleCount] — số cột luôn hiển thị (vd. #)
 */
export function countVisibleScreenColumns(hiddenCols, columns, fixedVisibleCount = 1) {
  const hidden = new Set(hiddenCols || []);
  return fixedVisibleCount + columns.filter((c) => !hidden.has(c.key)).length;
}

/**
 * Kết hợp ẩn theo cài đặt user và ẩn theo quyền (màn đơn hàng).
 */
export function combinedScreenColumnClass(hiddenCols, columnKey, lockedKeys = []) {
  if (Array.isArray(lockedKeys) && lockedKeys.includes(columnKey)) return "d-none";
  return screenColumnClass(hiddenCols, columnKey);
}
